import type { Student } from "@/types";
import type { LinePattern } from "./apartmentLines";

export interface InferredPattern {
  pattern: LinePattern;
  confidence: number; // 0 ~ 1
  evidence: string;
  sampleSize: number;
  maxUnit: number;
  maxFloor: number;
}

/**
 * 같은 단지 학생들의 호수 분포로 라인 패턴을 자동 추론.
 *
 * 추론 규칙 (한국 아파트 구조 통계):
 *  - unit max ≤ 2  → 1-2|3-4 (판상형, 2세대/층)            conf 0.9
 *  - unit max ≤ 4  → 1-2|3-4 (판상형, 3-4세대/층)           conf 0.8
 *  - unit max 5-8  → 1-2-3-4 (타워형, 4+세대/층 공용엘리)     conf 0.6
 *  - unit max 9-11 → corridor (중간복도식)                    conf 0.7
 *  - unit max ≥ 12 → corridor (복도식)                        conf 0.95
 *
 * 신뢰도는 샘플 크기(sample size)에 의해 보정: n<5는 -0.2, n>20은 +0.1
 */
export function inferLinePattern(students: Student[]): InferredPattern {
  const hoNumbers = students
    .map((s) => (s.buildingHo || "").trim())
    .filter(Boolean)
    .map((h) => Number(h.replace(/\D/g, "")))
    .filter((n) => Number.isFinite(n) && n > 0);

  if (!hoNumbers.length) {
    return {
      pattern: "1-2|3-4",
      confidence: 0,
      evidence: "호수 데이터 없음 — 기본값 적용",
      sampleSize: 0,
      maxUnit: 0,
      maxFloor: 0,
    };
  }

  // 각 호수 → floor + unit 분해
  const parsed = hoNumbers.map((n) => {
    const s = String(n);
    if (s.length <= 2) return { floor: 0, unit: n };
    const unit = Number(s.slice(-2));
    const floor = Number(s.slice(0, -2));
    return { floor, unit };
  });

  const maxUnit = Math.max(...parsed.map((p) => p.unit));
  const maxFloor = Math.max(...parsed.map((p) => p.floor));
  const uniqueUnits = new Set(parsed.map((p) => p.unit));

  let pattern: LinePattern;
  let base: number;
  let reason: string;

  if (maxUnit <= 2) {
    pattern = "1-2|3-4"; base = 0.9;
    reason = "관측 최대 unit ≤ 2 → 판상형 2호/층";
  } else if (maxUnit <= 4 && uniqueUnits.size <= 4) {
    pattern = "1-2|3-4"; base = 0.8;
    reason = "관측 max unit ≤ 4 → 판상형 (1·2호 / 3·4호 라인 분리)";
  } else if (maxUnit <= 8) {
    pattern = "1-2-3-4"; base = 0.6;
    reason = `관측 max unit = ${maxUnit} → 타워형 또는 4+세대/층 (코어 공용)`;
  } else if (maxUnit >= 12) {
    pattern = "corridor"; base = 0.95;
    reason = `관측 max unit = ${maxUnit} → 복도식 (층당 다수 세대)`;
  } else {
    pattern = "corridor"; base = 0.7;
    reason = `관측 max unit = ${maxUnit} → 중간 복도식 가능성`;
  }

  // 샘플 크기 보정
  const n = hoNumbers.length;
  let confidence = base;
  if (n < 5) confidence -= 0.2;
  else if (n > 20) confidence = Math.min(1, confidence + 0.1);

  return {
    pattern,
    confidence: Math.max(0, Math.min(1, confidence)),
    evidence: reason,
    sampleSize: n,
    maxUnit,
    maxFloor,
  };
}

/**
 * 학생 배열을 단지(아파트명) 기준으로 그룹핑하고 각 단지의 추론 패턴 계산.
 */
export function buildApartmentIndex(students: Student[]): ApartmentAggregate[] {
  const groups = new Map<string, Student[]>();
  for (const s of students) {
    if (!s.buildingName) continue;
    const key = `${s.gu || ""}|${s.dong || ""}|${s.buildingName}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(s);
  }

  const out: ApartmentAggregate[] = [];
  for (const [key, items] of groups) {
    const [gu, dong, name] = key.split("|");
    const buildings = Array.from(new Set(items.map((s) => s.buildingDong).filter(Boolean))).sort((a, b) => Number(a) - Number(b));
    const sampleHoByBuilding: Record<string, string[]> = {};
    for (const s of items) {
      const d = s.buildingDong || "-";
      if (!sampleHoByBuilding[d]) sampleHoByBuilding[d] = [];
      if (s.buildingHo && sampleHoByBuilding[d].length < 8) sampleHoByBuilding[d].push(s.buildingHo);
    }
    const inferred = inferLinePattern(items);
    out.push({
      id: encodeApartmentId(gu, dong, name),
      name,
      gu,
      dong,
      buildings,
      householdCount: items.length,
      sampleHoByBuilding,
      inferred,
      students: items,
    });
  }
  // 가구 수 많은 순
  out.sort((a, b) => b.householdCount - a.householdCount);
  return out;
}

export interface ApartmentAggregate {
  id: string;
  name: string;
  gu: string;
  dong: string;
  buildings: string[];
  householdCount: number;
  sampleHoByBuilding: Record<string, string[]>;
  inferred: InferredPattern;
  students: Student[];
}

export function encodeApartmentId(gu: string, dong: string, name: string): string {
  return `${gu}_${dong}_${name}`.replace(/[^\w가-힣-]/g, "_");
}

export const PATTERN_LABEL: Record<LinePattern, { short: string; description: string; emoji: string }> = {
  "1-2|3-4":   { short: "판상형 (1·2호 / 3·4호)", description: "1·2호 한 엘리베이터, 3·4호 다른 엘리베이터 — 대부분 아파트", emoji: "🏢" },
  "1-2-3-4":   { short: "타워형 (전층 공용)",      description: "1~4호 모두 한 엘리베이터 공용 — 주상복합·고층", emoji: "🗼" },
  "corridor":  { short: "복도식",                  description: "층당 다수 세대(6~12호+), 한 엘리베이터 공용 — 구축 아파트·빌라", emoji: "🏬" },
  "1|2|3|4":   { short: "호별 독립",               description: "각 호수마다 별도 엘리베이터 — 매우 드묾", emoji: "🚪" },
  "custom":    { short: "사용자 지정",              description: "수동 설정", emoji: "✏️" },
};
